import { IApiGateway } from '@/modules/api/api.interface'
import { IOrganizationRepository } from '@/modules/organization/core/organization.repository.interface'
import {
  EmailTemplateByOrganization,
  EmailTemplateResponse,
  OrganizationApiKeyResponse,
  OrganizationMinimalResponseDto,
  OrganizationsResponse,
} from '@/modules/organization/core/organization.types'
import { IdResponseDto } from '@/shared/domain/entities/common.entity'

import { CreateOrUpdateEmailTemplateData } from './organization.schema'

export class OrganizationRepository implements IOrganizationRepository {
  constructor(private api: IApiGateway) {}

  async getAll(): Promise<OrganizationsResponse> {
    return this.api.get<OrganizationsResponse>('organizations')
  }

  async getAllMinimal(): Promise<OrganizationMinimalResponseDto[]> {
    return this.api.get<OrganizationMinimalResponseDto[]>('organizations', {
      params: {
        output: 'minimal',
      },
    })
  }

  async create(body: FormData): Promise<IdResponseDto> {
    return this.api.post<IdResponseDto, FormData>('organizations', body, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async update(id: string, body: FormData): Promise<IdResponseDto> {
    return this.api.put<IdResponseDto, FormData>(`organizations/${id}`, body, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async getEmailTemplate(organizationId: string): Promise<EmailTemplateByOrganization> {
    return this.api.get<EmailTemplateByOrganization>(`organizations/${organizationId}/email-templates`)
  }

  async getEmailTemplates(organizationId: string): Promise<EmailTemplateResponse> {
    return this.api.get<EmailTemplateResponse>(`organizations/${organizationId}/email-templates`)
  }

  async createEmailTemplates(organizationId: string, body: CreateOrUpdateEmailTemplateData): Promise<IdResponseDto> {
    return this.api.post<IdResponseDto, CreateOrUpdateEmailTemplateData>(
      `organizations/${organizationId}/email-templates`,
      body,
    )
  }

  async updateEmailTemplates(
    organizationId: string,
    body: CreateOrUpdateEmailTemplateData,
    templateId: string,
  ): Promise<IdResponseDto> {
    return this.api.put<IdResponseDto, CreateOrUpdateEmailTemplateData>(
      `organizations/${organizationId}/email-templates/${templateId}`,
      body,
    )
  }

  async toggleEmailTemplates(
    organizationId: string,
    body: { isActive: boolean },
    templateId: string,
  ): Promise<IdResponseDto> {
    return this.api.put<IdResponseDto, { isActive: boolean }>(
      `organizations/${organizationId}/email-templates/${templateId}/toggle`,
      body,
    )
  }

  async generateApiKey(organizationId: string): Promise<OrganizationApiKeyResponse> {
    return this.api.put<OrganizationApiKeyResponse>(`organizations/${organizationId}/api-key`)
  }
}
